import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import moment from "moment";
import { path } from "ramda";
import { useFetch } from "hooks";
import { get, post } from "utils/api";
import { errorMessage } from "actions/message";
import CallDetailsModal from "components/screens/call-quality-dashboard/call-details-modal";

// Fetches + tracks the coach-analysis state for a single call. Pass either
// callSid or contactId — whichever the caller has. detailFetch is separate
// so the full CallDetailsModal payload only loads when the user asks for it.
export const useCoachAnalysis = ({ callSid, contactId, enabled = true }) => {
  const dispatch = useDispatch();
  const onFetchError = React.useCallback(
    prefix => err => dispatch(errorMessage(`${prefix}${err ? `: ${err}` : ""}`)),
    [dispatch]
  );

  const analysisFetch = useFetch({
    apiFn: params => {
      if (params.callSid) return get(`synthpop/analysis-state/${params.callSid}`);
      return get(`synthpop/analysis-state-by-contact/${params.contactId}`);
    },
    transformError: path(["response", "body", "status"]),
    onError: onFetchError("Could not load analysis state"),
  });
  const submitFetch = useFetch({
    apiFn: id => post(`synthpop/analyze-recording/${id}`),
    transformError: path(["response", "body", "status"]),
    onError: onFetchError("Could not submit call for analysis"),
  });
  const detailFetch = useFetch({
    apiFn: id => get(`synthpop/analysis-detail/${id}`),
    transformError: path(["response", "body", "status"]),
    onError: onFetchError("Could not load analysis detail"),
  });
  const [showDetail, setShowDetail] = React.useState(false);

  const key = callSid || contactId;
  React.useEffect(() => {
    if (!enabled || !key) return;
    analysisFetch.fetchData(callSid ? { callSid } : { contactId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);

  const onAnalyzeClick = async () => {
    const id = analysisFetch.response?.contactId ?? contactId;
    if (!id) return;
    const submitResult = await submitFetch.fetchData(id);
    // SubmitAsync returns { status, reason, ... } — surface non-Submitted
    // outcomes explicitly since the FE just re-fetches state on the happy path.
    if (submitResult && submitResult.status === "SubmitFailed") {
      dispatch(errorMessage(`Submission failed: ${submitResult.reason || "unknown"}`));
    }
    await analysisFetch.fetchData(callSid ? { callSid } : { contactId });
  };

  const onSeeAnalysisClick = async () => {
    const id = analysisFetch.response?.contactId ?? contactId;
    if (!id) return;
    // Skip refetch if we already have detail for this contact — resubmitting
    // the analysis clears everything at the parent level, so cache staleness
    // isn't a concern in practice.
    if (detailFetch.response?.contactId !== id) {
      await detailFetch.fetchData(id);
    }
    setShowDetail(true);
  };

  const dismissDetail = () => setShowDetail(false);

  return {
    state: analysisFetch.response,
    isFetching: analysisFetch.isFetching,
    isSubmitting: submitFetch.isFetching,
    onAnalyzeClick,
    detail: showDetail ? detailFetch.response : null,
    isDetailFetching: detailFetch.isFetching,
    onSeeAnalysisClick,
    dismissDetail,
  };
};

const CoachAnalysisControl = ({
  state,
  isFetching,
  isSubmitting,
  onAnalyzeClick,
  detail,
  isDetailFetching,
  onSeeAnalysisClick,
  dismissDetail,
}) => {
  if (isFetching || !state) return null;
  const { analysisUuid, submittedAt, postCallAnalysisId, contactId, notEligibleReason } = state;

  if (!analysisUuid) {
    if (notEligibleReason) return null;
    return (
      <button
        style={{ margin: 0, padding: 10 }}
        onClick={onAnalyzeClick}
        disabled={isSubmitting || !contactId}
      >
        {isSubmitting ? "Submitting…" : "Analyze Call"}
      </button>
    );
  }

  if (postCallAnalysisId) {
    return (
      <>
        <span className="coach-analysis-status">
          Analyzed {submittedAt && moment(submittedAt).format("MMM D, YYYY")}
        </span>
        <button
          style={{ margin: 0, padding: 10, marginLeft: 10 }}
          onClick={onSeeAnalysisClick}
          disabled={isDetailFetching}
        >
          {isDetailFetching ? "Loading…" : "See Analysis"}
        </button>
        {detail && <CallDetailsModal call={detail} onClose={dismissDetail} />}
      </>
    );
  }

  return (
    <span className="coach-analysis-status">Analysis in progress</span>
  );
};

CoachAnalysisControl.propTypes = {
  state: PropTypes.shape({
    analysisUuid: PropTypes.string,
    submittedAt: PropTypes.string,
    postCallAnalysisId: PropTypes.number,
    contactId: PropTypes.number,
    patientGuid: PropTypes.string,
    notEligibleReason: PropTypes.string,
  }),
  isFetching: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onAnalyzeClick: PropTypes.func.isRequired,
  detail: PropTypes.object,
  isDetailFetching: PropTypes.bool,
  onSeeAnalysisClick: PropTypes.func.isRequired,
  dismissDetail: PropTypes.func.isRequired,
};

export default CoachAnalysisControl;
